import React, { Suspense, useLayoutEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import Faq from "../pages/others/Faq";
import Terms from "../pages/others/Terms";
import StudentsPage from "../pages/users/StudentsPage";
import Error404Modern from "../pages/error/404-modern";
import EmployeesPage from "../pages/users/EmployeesPage";
import UsersPage from "../pages/users/UsersPage";
import StudentDetailsPage from "../pages/users/StudentDetailsPage";
import EmployeeDetailsPage from "../pages/users/EmployeeDetailsPage";
import UserDetailsPage from "../pages/users/UserDetailsPage";
import UserCreatePage from "../pages/users/UserCreatePage";
import StudentPageContextProvider from "../providers/StudentPageContextProvider";
import EmployeePageContextProvider from "../providers/EmployeePageContextProvider";
import UserPageContextProvider from "../providers/UserPageContextProvider";
import { useAuthContext } from "../hooks/useAuthContext";
import { USER_ROLES } from "../utilities/constants";
import NewsPageContextProvider from "../providers/NewsPageContextProvider";
import NewsPage from "../pages/news/NewsPage";
import NewsCreatePage from "../pages/news/NewsCreatePage";
import NewsCategoryPage from "../pages/newsCategory/NewsCategoryPage";
import NewsCategoryPageContextProvider from "../providers/NewsCategoryPageContextProvider";
import NewsCategoryCreatePage from "../pages/newsCategory/NewsCategoryCreatePage";
import NewsDetailsPage from "../pages/news/NewsDetailsPage";
import OlympiadPageContextProvider from "../providers/OlympiadPageContextProvider";
import OlympiadPage from "../pages/olympiad";
import OlympiadParticipantDetailsPage from "../pages/olympiad/details";
import OpenDaysDetailsPage from "../pages/opendays/details";
import OpenDaysContextProvider from "../providers/OpendaysContextProvider";
import OpenDaysPage from "../pages/opendays";
import SummerSchoolSpark from "../pages/summer-school/spark";
import SummerSchoolSparkDetails from "../pages/summer-school/spark/details";
import SummerSchoolResearch from "../pages/summer-school/research/index";
import SummerSchoolResearchDetails from "../pages/summer-school/research/details";
import SummerSchoolSparkProvider from "../providers/SummerSchoolSparkProvider";
import SummerSchoolResearchProvider from "../providers/SummerSchoolResearchProvider";
import PartnersPage from "../pages/partners/PartnersPage";
import PartnerCreatePage from "../pages/partners/PartnerCreatePage";
import PartnerDetailsPage from "../pages/partners/PartnerDetailsPage";
import PartnerPageContextProvider from "../providers/PartnerPageContextProvider";
import VacanciesPage from "../pages/vacancies/VacanciesPage";
import VacancyCreatePage from "../pages/vacancies/VacancyCreatePage";
import VacancyDetailsPage from "../pages/vacancies/VacancyDetailsPage";
import VacancyPageContextProvider from "../providers/VacancyPageContextProvider";

const Pages = () => {
  const { user } = useAuthContext();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  });

  return (
    <Suspense fallback={<div />}>
      <Routes>
        <Route path="*" element={<Error404Modern />} />
        {user?.roles?.length > 0 && (
          <Route
            path="/"
            element={
              user?.roles?.find((role) => role === USER_ROLES.ADMIN_STUDENT) ? (
                <Navigate to="/students" replace />
              ) : user?.roles?.find(
                  (role) => role === USER_ROLES.ADMIN_EMPLOYEE
                ) ? (
                <Navigate to="/employees" replace />
              ) : (
                <Navigate to="/news" replace />
              )
            }
          />
        )}
        <Route exact path={`/settings/terms-policy`} element={<Terms />} />
        <Route exact path={`/settings/faq`} element={<Faq />} />
        <Route
          exact
          path="/students"
          element={
            <StudentPageContextProvider>
              <StudentsPage />
            </StudentPageContextProvider>
          }
        />
        <Route
          path="/students/:id"
          element={
            <StudentPageContextProvider>
              <StudentDetailsPage />
            </StudentPageContextProvider>
          }
        />

        <Route
          exact
          path="/olympiad"
          element={
            <OlympiadPageContextProvider>
              <OlympiadPage />
            </OlympiadPageContextProvider>
          }
        />
        <Route
          exact
          path="/olympiad/:id"
          element={
            <OlympiadPageContextProvider>
              <OlympiadParticipantDetailsPage />
            </OlympiadPageContextProvider>
          }
        />

        <Route
          exact
          path="/summer-science-spark"
          element={
            <SummerSchoolSparkProvider>
              <SummerSchoolSpark />
            </SummerSchoolSparkProvider>
          }
        />
        <Route
          exact
          path="/summer-science-spark/:id"
          element={
            <SummerSchoolSparkProvider>
              <SummerSchoolSparkDetails />
            </SummerSchoolSparkProvider>
          }
        />
        <Route
          exact
          path="/summer-research-school"
          element={
            <SummerSchoolResearchProvider>
              <SummerSchoolResearch />
            </SummerSchoolResearchProvider>
          }
        />
        <Route
          exact
          path="/summer-research-school/:id"
          element={
            <SummerSchoolResearchProvider>
              <SummerSchoolResearchDetails />
            </SummerSchoolResearchProvider>
          }
        />

        <Route
          exact
          path="/opendays"
          element={
            <OpenDaysContextProvider>
              <OpenDaysPage />
            </OpenDaysContextProvider>
          }
        />

        <Route
          exact
          path="/opendays/:id"
          element={
            <OpenDaysContextProvider>
              <OpenDaysDetailsPage />
            </OpenDaysContextProvider>
          }
        />
        <Route
          exact
          path="/employees"
          element={
            <EmployeePageContextProvider>
              <EmployeesPage />
            </EmployeePageContextProvider>
          }
        />
        <Route exact path="/employees/:id" element={<EmployeeDetailsPage />} />
        <Route
          exact
          path="/users"
          element={
            <UserPageContextProvider>
              <UsersPage />
            </UserPageContextProvider>
          }
        />
        <Route
          exact
          path="/users/create"
          element={
            <UserPageContextProvider>
              <UserCreatePage />
            </UserPageContextProvider>
          }
        />
        <Route
          exact
          path="/users/:id"
          element={
            <UserPageContextProvider>
              <UserDetailsPage />
            </UserPageContextProvider>
          }
        />
        <Route
          exact
          path="/news"
          element={
            <NewsPageContextProvider>
              <NewsPage />
            </NewsPageContextProvider>
          }
        />
        <Route
          exact
          path="/news/:newsId"
          element={
            <NewsPageContextProvider>
              <NewsDetailsPage />
            </NewsPageContextProvider>
          }
        />
        <Route exact path="/news/create" element={<NewsCreatePage />} />
        <Route
          exact
          path="/news-category"
          element={
            <NewsCategoryPageContextProvider>
              <NewsCategoryPage />
            </NewsCategoryPageContextProvider>
          }
        />
        <Route
          exact
          path="/news-category/create"
          element={<NewsCategoryCreatePage />}
        />
        <Route
          exact
          path="/partners"
          element={
            <PartnerPageContextProvider>
              <PartnersPage />
            </PartnerPageContextProvider>
          }
        />
        <Route
          exact
          path="/partners/create"
          element={<PartnerCreatePage />}
        />
        <Route
          exact
          path="/vacancies"
          element={
            <VacancyPageContextProvider>
              <VacanciesPage />
            </VacancyPageContextProvider>
          }
        />
        <Route
          exact
          path="/vacancies/create"
          element={<VacancyCreatePage />}
        />
        <Route
          exact
          path="/vacancies/:vacancyId"
          element={
            <VacancyPageContextProvider>
              <VacancyDetailsPage />
            </VacancyPageContextProvider>
          }
        />
        <Route
          exact
          path="/partners/:partnerId"
          element={
            <PartnerPageContextProvider>
              <PartnerDetailsPage />
            </PartnerPageContextProvider>
          }
        />
      </Routes>
    </Suspense>
  );
};
export default Pages;
